import { registerMedia } from "../slices/mediaSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetches a Media resource by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 * - Logs an error AuditEvent if fetch fails
 *
 * @param {string} mediaId
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @param {boolean} options.forceFetch
 * @returns {object|null} Media resource or null if not found
 */
export const fetchMedia = async (
  mediaId,
  fhirClient,
  store,
  { forceFetch = false } = {}
) => {
  if (!mediaId || !fhirClient) return null;

  const cached = store.getState().media?.[mediaId];
  if (cached && !forceFetch) {
    return cached;
  }

  try {
    const fetched = await fhirClient.request(`Media/${mediaId}`);
    store.dispatch(registerMedia(fetched));
    return fetched;
  } catch (err) {
    console.error("❌ Failed to fetch Media:", err);
    const status = err?.response?.status || err?.status || 500;

    logErrorEvent({
      errorType: "read",
      error: {
        message: err?.message || "Media fetch failed",
        status,
        resourceId: `Media/${mediaId}`
      },
      description: `Failed to retrieve Media/${mediaId} from FHIR server`,
      store
    });

    return { error: status };
  }
};

/**
 * Fetches a Media resource by reference string (e.g., "Media/abc123")
 * - Validates format
 * - Extracts ID and delegates to fetchMedia
 *
 * @param {string} ref
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @returns {object|null} Media resource or null if not found
 */
export const fetchMediaByReference = async (
  ref,
  fhirClient,
  store,
  options = {}
) => {
  if (!ref || !ref.startsWith("Media/") || ref === "Media/undefined") {
    console.warn("⚠️ Invalid Media reference:", ref);
    return null;
  }

  const mediaId = ref.split("/")[1];
  if (!mediaId) {
    console.warn("⚠️ Could not extract media ID from reference:", ref);
    return null;
  }

  return fetchMedia(mediaId, fhirClient, store, options);
};
